/**
 * SchedulePopover — Gmail/Superhuman-style scheduled-send popover.
 *
 * Iter 71. Used by BOTH the Email composer and the GChat input so the
 * UX is identical across messaging surfaces. The parent passes a
 * callback that receives the chosen Date object — it's the parent's job
 * to actually persist the schedule (so this component stays
 * channel-agnostic).
 *
 * Quick presets per product spec:
 *   • Tomorrow at 8 AM
 *   • Tomorrow at 1 PM
 *   • Custom — opens the inline date+time picker
 *
 * The live human-readable label above the submit button updates every
 * 30s while the popover is open so "Will send in 2 hours" stays current.
 */
import { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Clock, CalendarDays, ChevronDown } from "lucide-react";

// ---------------------------------------------------------------------------
// Date helpers (no luxon/date-fns dep — keep bundle slim).
// ---------------------------------------------------------------------------
function setHM(d, h, m) {
  const out = new Date(d);
  out.setHours(h, m, 0, 0);
  return out;
}
function tomorrow8am() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return setHM(d, 8, 0);
}
function tomorrow1pm() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return setHM(d, 13, 0);
}

// "Will send in 2 days (Monday, June 15th at 8:00 AM)" / "Will send in 14 hours …"
function formatRelative(target) {
  if (!target) return "";
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return "Time is in the past — pick a later slot";
  const mins = Math.round(diffMs / 60000);
  const hrs  = Math.round(diffMs / 3600000);
  const days = Math.round(diffMs / 86400000);
  let rel;
  if (mins < 60) rel = `in ${mins} minute${mins === 1 ? "" : "s"}`;
  else if (hrs < 24) rel = `in ${hrs} hour${hrs === 1 ? "" : "s"}`;
  else rel = `in ${days} day${days === 1 ? "" : "s"}`;
  const weekday = target.toLocaleDateString(undefined, { weekday: "long" });
  const date    = target.toLocaleDateString(undefined, { month: "long", day: "numeric" });
  const time    = target.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `Will send ${rel} (${weekday}, ${date} at ${time} local time)`;
}

function fmtTimePreset(d) {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function SchedulePopover({
  open,
  onOpenChange,
  onConfirm,           // (Date) => void
  disabled = false,
  variant = "split",   // 'split' (email composer) | 'gchat-split' (blue) | 'menu' (gchat compact)
  triggerTestId = "schedule-toggle",
  channelLabel = "Schedule send",
  helperText = "Saves as a draft now and sends at the time you pick.",
}) {
  const [mode, setMode] = useState("presets");   // 'presets' | 'custom'
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  // Tick every 30 seconds so the relative label stays current.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [open]);

  // Reset to presets every time the popover re-opens.
  useEffect(() => {
    if (!open) return;
    setMode("presets");
    setDate(null);
    setTime("");
  }, [open]);

  const customTarget = useMemo(() => {
    if (!date || !time) return null;
    const [h, m] = time.split(":").map(Number);
    const out = new Date(date);
    if (Number.isFinite(h) && Number.isFinite(m)) out.setHours(h, m, 0, 0);
    return out;
  }, [date, time]);

  const presets = useMemo(() => ([
    { id: "t8",  label: "Tomorrow at 8:00 AM", target: tomorrow8am() },
    { id: "t1",  label: "Tomorrow at 1:00 PM", target: tomorrow1pm() },
  ]), []);

  const handlePresetClick = (preset) => {
    onConfirm?.(preset.target);
    onOpenChange?.(false);
  };
  const handleCustomConfirm = () => {
    if (!customTarget) return;
    onConfirm?.(customTarget);
    onOpenChange?.(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          data-testid={triggerTestId}
          disabled={disabled}
          className={
            variant === "split"
              ? "bg-slate-900 hover:bg-slate-800 text-white rounded-r-md rounded-l-none px-2 h-9 border-l border-slate-700/40"
              : variant === "gchat-split"
              // Iter 78 — Matches the Gmail-blue Send button height (h-8)
              // and tone exactly; the chevron half is a touch darker so
              // there's a clear split-button affordance without breaking
              // the unified blue chrome.
              ? "bg-[#0a4dba] hover:bg-[#093d96] text-white rounded-r-md rounded-l-none px-2 h-8 border-l border-white/25"
              : "bg-transparent hover:bg-slate-100 text-slate-600 h-8 w-8 p-0 rounded"
          }
          title={channelLabel}
        >
          <ChevronDown className="h-3.5 w-3.5"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 z-[70]" data-testid="schedule-popover">
        <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex items-center gap-1.5">
          <Clock className="h-3 w-3 text-slate-500"/>
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{channelLabel}</span>
        </div>
        {mode === "presets" && (
          <div className="py-2" data-testid="schedule-presets">
            <p className="px-4 py-1 text-[11px] text-slate-500 leading-snug">{helperText}</p>
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePresetClick(p)}
                data-testid={`schedule-preset-${p.id}`}
                className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center justify-between text-[13px] text-slate-700"
              >
                <span>{p.label}</span>
                <span className="text-[11px] text-slate-400">{fmtTimePreset(p.target)}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setMode("custom")}
              data-testid="schedule-preset-custom"
              className="w-full text-left px-4 py-2 hover:bg-slate-50 text-[13px] text-blue-600 font-medium border-t border-slate-100"
            >Pick custom date &amp; time…</button>
          </div>
        )}
        {mode === "custom" && (
          <div className="p-3 space-y-3" data-testid="schedule-custom">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                <CalendarDays className="h-3 w-3"/>Date
              </Label>
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                data-testid="schedule-calendar"
                fromDate={new Date()}
                className="rounded-md border border-slate-200 p-2"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                <Clock className="h-3 w-3"/>Time
              </Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="schedule-time"
                className="h-8 text-xs"
              />
            </div>
            {/* Live human-readable label */}
            <div
              data-testid="schedule-relative-label"
              className="text-[11px] text-slate-500 leading-snug min-h-[1.4em]"
            >
              {customTarget ? formatRelative(customTarget) : "Pick a date and time to see when it'll send."}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setMode("presets")}
                className="h-8 text-xs"
                data-testid="schedule-back-to-presets"
              >Back</Button>
              <Button
                type="button"
                onClick={handleCustomConfirm}
                data-testid="schedule-confirm"
                disabled={!customTarget}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs"
              >Schedule send</Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Helper for parents that want to render the "Scheduled for [time]" label
// on the main Send button once a schedule is locked in.
export function describeScheduled(target) {
  if (!target) return "";
  const d = target instanceof Date ? target : new Date(target);
  return d.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}
